import { FormikProps, useFormik } from 'formik';
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageTypes } from '../../../../store/declarations/enums/common/declaration-types';

export interface TemplateResponse {
    id: string;
    templateName?: string;
    template?: Template;
    declarationInternalType?: string;
    declarationExternalType?: string;
    declarationName?: MessageTypes;
    customerId?: string;
}

export type TemplateFormType = 'master' | 'product' | 'houseConsignment' | 'houseGoodsItem';

export interface TemplateOptions {
    country?: 'IRELAND' | 'UK' | 'EU' | 'CDS' | 'REVENUE' | 'HMRC';
    declarationType?: 'IMPORT' | 'EXPORT' | 'ENS' | 'NCTS';
    formType?: MessageTypes;
    name?: string;
    teamId?: string;
}
interface OpenModalArgs extends TemplateOptions {
    templateId?: string;
    viewOnly?: boolean;
}

export type TemplateContextType = {
    template?: boolean;
    setTemplate?: Dispatch<SetStateAction<boolean>>;
    templateFormik?: TemplateFormik;
    openModal?: (args?: OpenModalArgs | undefined) => void;
    closeModal?: () => void;
    open?: boolean;
    templateId?: string;
    options?: TemplateOptions;
    form?: TemplateFormType;
    setForm?: Dispatch<SetStateAction<TemplateFormType>>;
    isViewOnly?: boolean | undefined;
};
export const TemplateContext = createContext<TemplateContextType>({} as TemplateContextType);
export const useTemplateContext = () => useContext<TemplateContextType>(TemplateContext);

export interface MetaData {
    isViewable?: boolean;
    isEditable?: boolean;
}

export interface Template {
    master: TemplateData;
    product: TemplateData;
    houseConsignment?: TemplateData;
    houseGoodsItem?: TemplateData;
}
export interface TemplateData {
    defaults: Record<string, any>;
    meta: Record<string, MetaData>;
}
type TemplateFormik = FormikProps<Template>;

interface Props {
    children: ReactNode;
}

export const TemplateContextProvider = ({ children }: Props) => {
    const navigate = useNavigate();
    const [template, setTemplate] = useState(false);
    const [templateId, setTemplateId] = useState<string | undefined>(undefined);
    const [options, setOptions] = useState<OpenModalArgs | undefined>(undefined);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState<TemplateFormType>('master');
    const [isViewOnly, setIsViewOnly] = useState<boolean | undefined>(false);
    const templateMockData = {
        master: {
            defaults: {},
            meta: {},
        },
        product: {
            defaults: {},
            meta: {},
        },
    };
    const templateFormik = useFormik<Template>({
        initialValues: templateMockData,
        onSubmit: () => {},
    });

    const openModal = (args: OpenModalArgs | undefined) => {
        const { templateId, viewOnly, ...options } = args ?? {};
        setOpen(true);
        setTemplateId(templateId);
        setOptions(options);
        setIsViewOnly(viewOnly);
    };
    const closeModal = () => {
        setOpen(false);
        setForm('master');
        setTemplateId(undefined);
        navigate('/templates');
    };

    return (
        <TemplateContext.Provider
            value={{
                template,
                setTemplate,
                templateFormik,
                openModal,
                closeModal,
                open,
                templateId,
                options,
                form,
                setForm,
                isViewOnly,
            }}
        >
            {children}
        </TemplateContext.Provider>
    );
};
